import {DEL} from "js-vextensions";
import {CreateAccessor} from "mobx-graphlink";
import {TimeToString} from "web-vcore";
import {GetEntities_WithUserTag} from "../entities.js";
import {Entity} from "../entities/@Entity.js";
import {GetJournalEntries, GetJournalEntry} from "../journalEntries.js";
import {MeID} from "../users.js";
import {JournalEntry, JournalSegment} from "./@JournalEntry.js";
import {EstimateSegmentStartTime} from "../@Shared/JournalTimeEstimates.js";

export function EnsureSegmentUsingNewAnchorEntitiesField(segment: JournalSegment) {
	segment.VSet({
		anchorEntity: DEL, // if an anchor-entity had been stored in old prop, delete it here...
		anchorEntities: (segment.anchorEntities ?? [])
			// ..and reinclude it at the end of the new anchors list
			.concat(segment.anchorEntity ? [segment.anchorEntity] : []),
	});
}

export const GetDreamAnchorEntity = CreateAccessor((dream: JournalSegment|n, targetAnchorEntityIndex = 0)=>{
	return GetDreamAnchorEntities(dream)[targetAnchorEntityIndex] ?? null;
});
export const GetDreamAnchorEntities = CreateAccessor((segment: JournalSegment|n)=>{
	if (segment == null) return [];
	const result = [] as string[];
	if (segment.anchorEntities) result.push(...segment.anchorEntities);
	if (segment.anchorEntity) result.push(segment.anchorEntity); // add this after, to match with update logic in JournalSegmentUI
	return result;
});

/*export const GetDreamTitle = CreateAccessor((dream: JournalSegment)=>{
	if (dream.longText.startsWith("# ")) {
		const title = dream.longText.split("\n")[0].slice(1).trim();
		if (title.length) return title;
	}
	return null;
});*/

export const GetDreamTitle = CreateAccessor((dream: JournalSegment, targetTitleIndex = 0)=>{
	return GetDreamTitles(dream)[targetTitleIndex] ?? null;
});
export const GetDreamTitles = CreateAccessor((dream: JournalSegment)=>{
	const lines = dream.longText.split("\n");
	const titles = [] as string[];
	for (const line of lines) {
		if (line.startsWith("# ")) {
			const title = line.slice(2).trim();
			if (title.length) titles.push(title);
		}
	}
	return titles;
});

export class DreamScene {
	constructor(data: Omit<DreamScene, "ID">) {
		Object.assign(this, data);
	}
	//id: string; // `${journalEntryID}/${segmentID}/${sceneIndex}`
	ID() {
		return `${this.journalEntry.id}/${this.segment.id}/${this.sceneIndex ?? 0}`;
	}
	journalEntry: JournalEntry;
	segment: JournalSegment;
	/*#* If scene-index is not explicitly provided, preserve this info by leaving null (readers can fallback to 0) */
	//sceneIndex: number|n;
	sceneIndex: number;
	sceneTitle: string;
	sceneAnchorId: string|n;
	//sceneAnchor: Entity|n;
}
export const GetStandardDeepOrderingKeyForDreamScene = (scene: DreamScene)=>{
	const segmentStartTime = EstimateSegmentStartTime(scene.journalEntry, scene.segment);
	return `${TimeToString(segmentStartTime)}/${scene.sceneIndex ?? 0}`;
};
export const GetDreamSceneForJournalPath = CreateAccessor((journalPath: string): DreamScene|null=>{
	const [entryID, segmentID, sceneIndexStr] = journalPath.split("/");
	const entry = GetJournalEntry(entryID);
	if (entry == null) return null;
	const segment = entry.segments.find(a=>a.id == segmentID);
	if (segment == null) return null;
	const scenes = GetScenesForJournalEntryAndSegment(entry, segment);
	const sceneIndex = parseInt(sceneIndexStr).NaNTo(null);
	if (sceneIndex == null) return null;
	return scenes[sceneIndex] ?? null;
});

export const dreamCellLetters = "abcdefghijklmnopqrstuvwxyz".split(""); // the 26 letters, one per dream-cell
export function FindLetterAnchorEntity(anchorEntities: Entity[], letter: string) {
	return anchorEntities
		.filter(a=>a.name.toLowerCase()[0] == letter && (a.image_embedded || a.url))
		.OrderBy(a=>a.createdAt).LastOrX();
}
export const GetAllLetterAnchorEntities = CreateAccessor(()=>{
	const anchorEntities = GetEntities_WithUserTag("anchor");
	const anchorsMap_all = dreamCellLetters.ToMap(l=>l, l=>FindLetterAnchorEntity(anchorEntities, l));
	const anchorsMap_valid = new Map([...anchorsMap_all].filter(([_, v])=>v != null) as [string, Entity][]);
	const anchorsList_validIDs = [...anchorsMap_valid.values()].map(a=>a.id);
	return {anchorsMap_all, anchorsMap_valid, anchorsList_validIDs};
});


/*export type JournalSegmentWithEntry = JournalSegment & {journalEntry: JournalEntry};
export const SegmentWithEntry = (segment: JournalSegment, entry: JournalEntry): JournalSegmentWithEntry=>{
	return {...segment, journalEntry: entry};
};*/
export type EntryAndSegment = {entry: JournalEntry, segment: JournalSegment};
export const CombineEntryAndSegment = (entry: JournalEntry, segment: JournalSegment): EntryAndSegment=>{
	return {entry, segment};
};
// todo: maybe change this into a general "GetAllDreamScenes" accessor; and then everything else that currently creates DreamScene's, instead derives from this result-set
export const GetScenesValidToBeLetterAnchors = (entries: JournalEntry[])=>{
	const dreams = entries.flatMap(a=>a.segments.map(b=>CombineEntryAndSegment(a, b)))
		.filter(dream=>{
			if (dream.segment.wakeTime != null) return false;
			if (dream.segment.excludeFromIntegration) return false;
			if (dream.segment.longText.trim().length == 0) return false;
			return true;
		}).OrderBy(dream=>{
			return EstimateSegmentStartTime(dream.entry, dream.segment);
		});
	return dreams.flatMap(dream=>{
		if (dream.segment.id == null) return []; // just ignore super old segments without ids
		return GetScenesForJournalEntryAndSegment(dream.entry, dream.segment);
	});
};

export const GetLastXDreamScenesPerLetterAnchor = CreateAccessor((scenesPerAnchor: number)=>{
	const letterAnchors = GetAllLetterAnchorEntities();
	const entries = GetJournalEntries(MeID());
	const validScenes = GetScenesValidToBeLetterAnchors(entries);

	const result = new Map<string, DreamScene[]>();
	for (const [letter, anchor] of letterAnchors.anchorsMap_valid) {
		const lastXScenesWithAnchor = validScenes.filter(scene=>scene.sceneAnchorId == anchor.id).slice(-scenesPerAnchor);
		result.set(letter, lastXScenesWithAnchor);
	}
	return result;
});

export const GetScenesForJournalEntryAndSegment = CreateAccessor((entry: JournalEntry, segment: JournalSegment)=>{
	const sceneTitles = GetDreamTitles(segment);
	return sceneTitles.map((title, index)=>new DreamScene({
		journalEntry: entry,
		segment: segment,
		sceneIndex: index,
		sceneTitle: title,
		sceneAnchorId: GetDreamAnchorEntity(segment, index),
	}));
});